import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvService } from '@lib/env';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { IFileUploadMaxSize } from '@lib/upload-zone/ui';
import { AccountingChange, AccountingType } from '@lib/accounting';
import { UriBuilder } from 'uribuilder';
import {
    AccountingDataRequest,
    AddAttachmentsRequest,
    AddBasketItemAttachmentsRequest,
    Attachments,
    Basket,
    BasketItem,
    BasketItemRequest,
    ClassificationDataRequest,
    CompanyInformation,
    CompanySetting,
    DeleteAttachmentBasketItemFileRequest,
    DeleteAttachmentFileRequest,
    SupplierNote,
    UpdateArticleNumberDataRequest,
    UpdateDesiredDeliveryDateDataRequest,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { HttpExtras } from '@lib/core';

@Injectable({ providedIn: 'root' })
export class SupplierBasketService {
    private readonly env = inject(EnvService);
    private readonly http = inject(HttpClient);

    private readonly basketSidenavOpen$: BehaviorSubject<boolean>;

    constructor() {
        this.basketSidenavOpen$ = new BehaviorSubject<boolean>(false);
    }

    getSupplierNote(supplierId: string): Observable<SupplierNote> {
        return this.http.get<SupplierNote>(this.env.getBase('basket', 'current', supplierId).toString());
    }

    getBasket(): Observable<Basket> {
        return this.http.get<Basket>(this.env.getBase('basket', 'current').toString(), {
            params: HttpExtras.IGNORE_CACHE,
        });
    }

    updateSupplierComment(comment: string, supplierId: string): Observable<Basket> {
        return this.http.put<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierId, 'comment-for-supplier').toString(),
            { commentForSupplier: comment },
        );
    }

    updateInternalPositionNote(positionNote: string, supplierId: string, basketItemId: number): Observable<Basket> {
        return this.http.put<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierId,
                    'basket-items',
                    basketItemId,
                    'internal-position-note',
                )
                .toString(),
            { internalNote: positionNote },
        );
    }

    updateDesiredDeliveryDate(desiredDeliveryDateInfoSB: UpdateDesiredDeliveryDateDataRequest): Observable<Basket> {
        const supplierBasketId = desiredDeliveryDateInfoSB.supplierBasketId;
        const basketItemId = desiredDeliveryDateInfoSB.basketItemId;
        const desiredDeliveryDate = desiredDeliveryDateInfoSB.desiredDeliveryDate;
        return this.http.put<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierBasketId,
                    'basket-items',
                    basketItemId,
                    'desired-delivery-date',
                )
                .toString(),
            {
                desiredDeliveryDate,
            },
        );
    }

    updatePersonComment(comment: string, supplierId: string): Observable<Basket> {
        return this.http.put<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierId, 'comment-for-approver').toString(),
            { commentForApprover: comment },
        );
    }

    updateProductNumber(updateArticleNumberDataRequest: UpdateArticleNumberDataRequest): Observable<Basket> {
        const supplierBasketId = updateArticleNumberDataRequest.supplierBasketId;
        const basketItemId = updateArticleNumberDataRequest.basketItemId;
        const uri: UriBuilder = this.env.getBase(
            'basket',
            'current',
            'supplier-baskets',
            supplierBasketId,
            'basket-items',
            basketItemId,
            'product',
        );
        uri.query['productNumber'] = updateArticleNumberDataRequest.articleNumber;
        return this.http.put<Basket>(uri.toString(), { params: updateArticleNumberDataRequest.articleNumber });
    }

    deleteBasketItem(item: BasketItemRequest): Observable<Basket> {
        const supplierBasketId = item.supplierBasketId;
        const basketItemId = item.basketItem.id;
        return this.http.delete<Basket>(
            this.env
                .getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'basket-items', basketItemId)
                .toString(),
        );
    }

    updateBasketAmount(item: BasketItemRequest): Observable<Basket> {
        const supplierBasketId = item.supplierBasketId;
        const basketItemId = item.basketItem.id;
        return this.http.put<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierBasketId,
                    'basket-items',
                    basketItemId,
                    'quantity',
                )
                .toString(),
            {
                quantityValue: this.checkAmount(
                    item.basketItem.quantity.value,
                    item.basketItem.product.minimumOrderQuantityValue,
                    item.basketItem.product.quantityInterval,
                ),
            },
        );
    }

    addAttachments(data: AddAttachmentsRequest): Observable<Attachments[]> {
        const supplierBasketId = data.supplierBasketId;
        return this.http.post<Attachments[]>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'attachments').toString(),
            data.attachments,
            { params: new HttpExtras({ customErrorhandling: false }) },
        );
    }

    addBasketItemAttachments(data: AddBasketItemAttachmentsRequest): Observable<Attachments[]> {
        const supplierBasketId = data.supplierBasketId;
        const basketItemId = data.item.id;

        return this.http.post<Attachments[]>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierBasketId,
                    'basket-items',
                    basketItemId,
                    'attachments',
                )
                .toString(),
            data.attachments,
            { params: new HttpExtras({ customErrorhandling: false }) },
        );
    }

    deleteAttachmentFile(attachmentFile: DeleteAttachmentFileRequest): Observable<Attachments> {
        const supplierBasketId = attachmentFile.supplierBasketId;
        const attachmentId = attachmentFile.attachmentId;
        return this.http.delete<Attachments>(
            this.env
                .getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'attachments', attachmentId)
                .toString(),
        );
    }

    deleteAttachmentBasketItemFile(attachmentFile: DeleteAttachmentBasketItemFileRequest): Observable<Attachments> {
        const supplierBasketId = attachmentFile.supplierBasketId;
        const basketItemId = attachmentFile.basketItemId;
        const attachmentId = attachmentFile.attachmentId;
        return this.http.delete<Attachments>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierBasketId,
                    'basket-items',
                    basketItemId,
                    'attachments',
                    attachmentId,
                )
                .toString(),
        );
    }

    saveAccountingValue(accountingData: AccountingDataRequest): Observable<Basket> {
        const supplierBasketId = accountingData.basketItemId;
        const supplierId = accountingData.supplierId;
        const accountingSelectedId = accountingData.accountingChangeData.accountAssignmentId;

        if (typeof accountingSelectedId !== 'number') {
            return throwError(
                () => new Error('accountingChangeData.accountAssignmentId is required to save a basket item accounting'),
            );
        }

        return this.http.put<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierId,
                    'basket-items',
                    supplierBasketId,
                    this.getPathByAccountingType(accountingData.accountingChangeData),
                    accountingSelectedId,
                )
                .toString(),
            {},
        );
    }

    deleteAccountingValue(accountingDeleteData: AccountingDataRequest): Observable<Basket> {
        const basketItemId = accountingDeleteData.basketItemId;
        const supplierId = accountingDeleteData.supplierId;
        const accountingSelectedId = accountingDeleteData.accountingChangeData.customAccountingTypeId as number;

        const baseUrl = this.env.getBase(
            'basket',
            'current',
            'supplier-baskets',
            supplierId,
            'basket-items',
            basketItemId,
            this.getPathByAccountingType(accountingDeleteData.accountingChangeData),
        );

        let url: string;

        if (accountingDeleteData.accountingChangeData.type === AccountingType.CUSTOM) {
            url = `${baseUrl}/${accountingSelectedId}`;
        } else {
            url = baseUrl.toString();
        }

        return this.http.delete<Basket>(url);
    }

    saveClassificationValue(classificationData: ClassificationDataRequest): Observable<Basket> {
        const supplierBasketId = classificationData.basketItemId;
        const supplierId = classificationData.supplierId;
        const classificationId = classificationData.classificationChangeData.irdicc;

        return this.http.put<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierId,
                    'basket-items',
                    supplierBasketId,
                    'classification-code',
                    encodeURIComponent(classificationId),
                )
                .toString(),
            {},
        );
    }

    deleteClassificationValue(classificationDeleteData: ClassificationDataRequest): Observable<Basket> {
        const basketItemId = classificationDeleteData.basketItemId;
        const supplierId = classificationDeleteData.supplierId;

        return this.http.delete<Basket>(
            this.env
                .getBase(
                    'basket',
                    'current',
                    'supplier-baskets',
                    supplierId,
                    'basket-items',
                    basketItemId,
                    'classification-code',
                )
                .toString(),
        );
    }

    getContactPerson(supplierBaksetId: string): Observable<CompanyInformation[]> {
        const uri: UriBuilder = this.env.getBase('suppliers', 'contacts');
        uri.query['supplierCompanyId'] = supplierBaksetId;
        uri.query['excludeNonNotifiableContacts'] = true;

        return this.http.get<CompanyInformation[]>(uri.toString());
    }

    existInSupplierWarehouse(supplierBasket: BasketItem): Observable<boolean> {
        const productArticleNumber = supplierBasket.product?.supplierProductNumber;
        const supplierCompanyId = supplierBasket.product.supplier.id;

        const uri: UriBuilder = this.env.getBase('warehouse', 'inventory', 'article');
        uri.query['sku'] = productArticleNumber;
        uri.query['supplier_company_id'] = supplierCompanyId;

        return this.http.get<boolean>(uri.toString());
    }

    getQuantityInStock(supplierBasket: BasketItem): Observable<boolean> {
        const productArticleNumber = supplierBasket.product?.supplierProductNumber;
        const supplierCompanyId = supplierBasket.product.supplier.id;

        const uri: UriBuilder = this.env.getBase('warehouse', 'inventory', 'article', 'quantity');
        uri.query['sku'] = productArticleNumber;
        uri.query['supplier_company_id'] = supplierCompanyId;

        return this.http.get<boolean>(uri.toString());
    }

    isSpecifyDeliveryDate(): Observable<CompanySetting> {
        const uri: UriBuilder = this.env.getBase('customer', 'company', 'settings');

        return this.http.get<CompanySetting>(uri.toString());
    }

    getMaxUploadSize(): Observable<IFileUploadMaxSize> {
        return this.http.get<IFileUploadMaxSize>(this.env.getBase('file', 'limit').toString());
    }

    private getPathByAccountingType(accountingChange: AccountingChange): string {
        switch (accountingChange.type) {
            case AccountingType.COST_CENTER:
                return 'cost-center';

            case AccountingType.GENERAL_LEDGER_ACCOUNT:
                return 'general-ledger-account';

            case AccountingType.PROJECT:
                return 'project';

            case AccountingType.CUSTOM:
                return 'custom-account-assignments';

            default:
                return '';
        }
    }

    private checkAmount(quantity: number, minimumOrderQuantity: number, interval: number): number {
        if (quantity < minimumOrderQuantity) {
            return minimumOrderQuantity;
        }
        if ((quantity - minimumOrderQuantity) % interval !== 0) {
            return quantity - ((quantity - minimumOrderQuantity) % interval) + interval;
        }

        return quantity;
    }
}
